import React, { useState, useEffect } from "react";
import {
  Search,
  BookOpen,
  Link as LinkIcon,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  PlayCircle,
  StickyNote,
  Save,
} from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Textarea } from "../../ui/textarea";
import { db } from "../../../lib/db";
import { toast } from "sonner";
import { formatDate, getDaysUntil, isOverdue } from "../../../lib/dateUtils";
import { useAuthStore } from "../../../store/authStore";

export default function MyCoursesPage() {
  const { user } = useAuthStore();
  const [courses, setCourses] = useState([]);
  const [myAllocations, setMyAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  // Notes Modal State
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [activeAllocation, setActiveAllocation] = useState(null);
  const [noteContent, setNoteContent] = useState("");

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [coursesData, allocationsData] = await Promise.all([
        db.courses.toArray(),
        db.courseAllocations.toArray(),
      ]);

      const myAllocs = allocationsData.filter((a) => a.userId === user?.id);
      setMyAllocations(myAllocs.sort((a, b) => a.dueDate - b.dueDate));
      setCourses(coursesData);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (allocationId, newStatus) => {
    try {
      await db.courseAllocations.update(allocationId, {
        status: newStatus,
        updatedAt: Date.now(),
      });
      toast.success(`Course marked as ${newStatus}`);
      loadData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const openNotesModal = (allocation) => {
    setActiveAllocation(allocation);
    setNoteContent(allocation.notes || "");
    setShowNotesModal(true);
  };

  const handleSaveNote = async () => {
    if (!activeAllocation) return;

    try {
      await db.courseAllocations.update(activeAllocation.id, {
        notes: noteContent,
        updatedAt: Date.now(),
      });
      toast.success("Notes saved successfully");
      setShowNotesModal(false);
      loadData();
    } catch (error) {
      toast.error("Failed to save notes");
    }
  };

  const getCourseById = (id) => courses.find((c) => c.id === id);

  const filteredAllocations = myAllocations.filter((allocation) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "overdue")
      return isOverdue(allocation.dueDate) && allocation.status !== "completed";
    return allocation.status === statusFilter;
  });

  const stats = {
    total: myAllocations.length,
    completed: myAllocations.filter((a) => a.status === "completed").length,
    inProgress: myAllocations.filter((a) => a.status === "started").length,
    pending: myAllocations.filter((a) => a.status === "pending").length,
  };

  const getStatusBadge = (status, dueDate) => {
    if (status === "completed")
      return (
        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-emerald-200">
          Completed
        </Badge>
      );
    if (status === "started")
      return (
        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 border-amber-200">
          In Progress
        </Badge>
      );
    if (isOverdue(dueDate))
      return (
        <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-200 border-rose-200">
          Overdue
        </Badge>
      );
    return (
      <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200">
        Pending
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Learning</h1>
        <p className="text-gray-500 mt-1">
          Track your assigned courses and progress
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">
                  Assigned Courses
                </p>
                <p className="text-3xl font-bold text-blue-900">
                  {stats.total}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/60 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">
                  In Progress
                </p>
                <p className="text-3xl font-bold text-amber-900">
                  {stats.inProgress}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/60 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-600">
                  Completed
                </p>
                <p className="text-3xl font-bold text-emerald-900">
                  {stats.completed}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/60 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-gray-500" />
              Course List
            </h3>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="started">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            {filteredAllocations.map((allocation) => {
              const course = getCourseById(allocation.courseId);
              const daysUntil = getDaysUntil(allocation.dueDate);

              if (!course) return null;

              return (
                <div
                  key={allocation.id}
                  className="group flex flex-col md:flex-row md:items-start justify-between p-5 border border-gray-100 rounded-xl hover:border-blue-100 hover:shadow-md transition-all duration-200 bg-white gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                        {course.title}
                      </h3>
                      {getStatusBadge(allocation.status, allocation.dueDate)}
                    </div>

                    <p className="text-sm text-gray-600 mb-4 leading-relaxed max-w-2xl">
                      {course.description}
                    </p>

                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        Due:{" "}
                        <span className="font-medium text-gray-700">
                          {formatDate(allocation.dueDate)}
                        </span>
                      </span>

                      {allocation.status !== "completed" && (
                        <span
                          className={`flex items-center gap-1.5 px-2 py-1 rounded-md border ${
                            isOverdue(allocation.dueDate)
                              ? "bg-rose-50 border-rose-100 text-rose-700"
                              : "bg-blue-50 border-blue-100 text-blue-700"
                          }`}
                        >
                          <Clock className="w-3.5 h-3.5" />
                          <span className="font-medium">
                            {Math.abs(daysUntil)} days{" "}
                            {isOverdue(allocation.dueDate)
                              ? "overdue"
                              : "remaining"}
                          </span>
                        </span>
                      )}

                      {allocation.notes && (
                        <span className="flex items-center gap-1.5 text-amber-600 bg-amber-50 px-2 py-1 rounded-md border border-amber-100">
                          <StickyNote className="w-3.5 h-3.5" />
                          <span className="font-medium">Has Notes</span>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-2 md:mt-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openNotesModal(allocation)}
                      className="border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700"
                    >
                      <StickyNote className="w-4 h-4 mr-2 text-gray-500" />
                      Notes
                    </Button>

                    <a
                      href={course.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center h-9 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded-md hover:bg-blue-100 hover:border-blue-200 transition-all shadow-sm"
                    >
                      <LinkIcon className="w-4 h-4 mr-2" />
                      Go to Course
                    </a>

                    {allocation.status === "pending" && (
                      <Button
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                        onClick={() => updateStatus(allocation.id, "started")}
                      >
                        <PlayCircle className="w-4 h-4 mr-2" /> Start Learning
                      </Button>
                    )}

                    {allocation.status === "started" && (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                        onClick={() => updateStatus(allocation.id, "completed")}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Complete
                      </Button>
                    )}

                    {allocation.status === "completed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled
                        className="bg-gray-50 text-gray-500 border-gray-200"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" /> Completed
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredAllocations.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <BookOpen className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  No courses found
                </h3>
                <p className="text-gray-500 mt-1 max-w-sm">
                  {statusFilter === "all"
                    ? "You haven't been assigned any courses yet."
                    : `No courses found with status "${statusFilter}".`}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Notes Modal */}
      <Dialog open={showNotesModal} onOpenChange={setShowNotesModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-amber-500" />
              Course Notes
            </DialogTitle>
            <DialogDescription>
              Add personal notes, key takeaways, or draft thoughts for
              <span className="font-medium text-gray-900">
                {" "}
                {
                  courses.find((c) => c.id === activeAllocation?.courseId)
                    ?.title
                }
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Textarea
              placeholder="Write your notes here..."
              className="min-h-[200px] resize-none text-base leading-relaxed p-4 bg-amber-50/30 border-amber-100 focus:border-amber-300 focus:ring-amber-100"
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
            />
            <div className="flex justify-between items-center mt-2 text-xs text-gray-400">
              <span>Markdown supported</span>
              <span>{noteContent.length} characters</span>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setShowNotesModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveNote}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
